import React from "react"
import { Box } from "@mui/material"
import { Role, ROLE_LABELS, getRoleLabel, UserRole } from "src/UserService"
import { StatusChip } from "src/components/table/StatusChip"
import { Order } from "src/components/table/SortableTableHeaderCell"
import type { CompanyMembersSortBy } from "src/services/api/companyMembers"
import { Member } from "./MembersTable"
import { resolveKnowledgeBaseName } from "src/services/api/user"

export interface MemberLike {
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  roles: Role[]
  profile?: string | null
}

export function filterMembersByQuery<T extends MemberLike>(members: T[], query: string): T[] {
  if (!query) return members
  const q = query.toLowerCase()
  return members.filter(
    (m) =>
      (m.firstName?.toLowerCase().includes(q) ?? false) ||
      (m.lastName?.toLowerCase().includes(q) ?? false) ||
      (m.email?.toLowerCase().includes(q) ?? false)
  )
}

export function filterMembersByRoles<T extends { roles: Role[] }>(members: T[], roles: Role[]): T[] {
  if (roles.length === 0) return members
  return members.filter((m) => m.roles.some((r) => roles.includes(r)))
}

function getMemberSortValue<T extends MemberLike>(member: T, field: string): string {
  if (field === "FIRST_NAME") return member.firstName ?? ""
  if (field === "EMAIL") return member.email ?? ""
  if (field === "PROFILE") return member.profile ?? ""
  if (field === "USER_NAME") return `${member.firstName ?? ""} ${member.lastName ?? ""}`
  return member.lastName ?? ""
}

export function sortMembersByField<T extends MemberLike>(members: T[], orderBy: string, order: "asc" | "desc"): T[] {
  return [...members].sort((a, b) => {
    const cmp = getMemberSortValue(a, orderBy).localeCompare(getMemberSortValue(b, orderBy))
    return order === "asc" ? cmp : -cmp
  })
}

export const COMPANY_MEMBERS_HIDDEN_ROLES: Role[] = [
  UserRole.PLAIN,
  UserRole.EXFLUENCY_ADMIN,
  UserRole.PLATFORM_MANAGER,
  UserRole.SUPPORT,
  UserRole.GATED_COMMUNITY_MANAGER,
  UserRole.GC_PROJECT_MANAGER
]

export const companyMembersRoleOptions: { id: Role; label: string }[] = (Object.keys(ROLE_LABELS) as UserRole[])
  .filter((id) => !COMPANY_MEMBERS_HIDDEN_ROLES.includes(id as Role))
  .map((id) => ({ id: id as Role, label: ROLE_LABELS[id] }))

export function mapSortDirection(order: Order): "ASCENDING" | "DESCENDING" {
  return order === "asc" ? "ASCENDING" : "DESCENDING"
}

export function renderRolesCell<TProfile extends string>(
  member: Member<TProfile>,
  palette: { primary: { light: string } }
): React.ReactNode {
  const visibleRoles = member.roles.filter((r) => !COMPANY_MEMBERS_HIDDEN_ROLES.includes(r))
  if (visibleRoles.length === 0) return "—"
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {visibleRoles.map((role) => (
        <StatusChip
          key={role}
          label={getRoleLabel(role).toUpperCase()}
          backgroundColor={palette.primary.light}
        />
      ))}
    </Box>
  )
}

export function renderCommunitiesCell(member: { communities?: { communityName: string }[] }, login: string): React.ReactNode {
  if (!member.communities || member.communities.length === 0) return "—"
  return (
    <Box component="span" sx={{ whiteSpace: "pre-line" }}>
      {member.communities.map((c) => resolveKnowledgeBaseName(c.communityName, login)).join("\n")}
    </Box>
  )
}

export type { CompanyMembersSortBy }
